/**
 * Hooks for the Microsoft (Outlook / 365) calendar integration. Mirror
 * of `useGoogleCalendar` so the UI can render both providers
 * symmetrically. The popup-based OAuth start uses the
 * `/api/microsoft/start` Cloud Run endpoint (Hosting rewrite).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useEffectiveUid, useIsAdminMode } from '../auth/TargetUidContext';

export interface MicrosoftAuthStatus {
  connected: boolean;
  calendarId?: string;
  scopes?: string[];
  hasRefreshToken?: boolean;
}

export function useMicrosoftAuthStatus() {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useQuery<MicrosoftAuthStatus>({
    queryKey: ['microsoft-auth-status', targetUid] as const,
    enabled: !!user && !!targetUid && !adminMode,
    // Admin-mode for Microsoft calendar is intentionally read-only via
    // future admin callable; for now we hide the connect/disconnect
    // buttons in admin view since the user must do the OAuth consent.
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, MicrosoftAuthStatus>(functions, 'getMicrosoftAuthStatus');
      const r = await fn({});
      return r.data;
    },
  });
}

export function useMicrosoftCalendars() {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const status = useMicrosoftAuthStatus();
  return useQuery({
    queryKey: ['microsoft-calendars', targetUid] as const,
    enabled: !!user && !!status.data?.connected,
    staleTime: 60_000,
    queryFn: async () => {
      const fn = httpsCallable<
        unknown,
        { items: Array<{ id: string; summary: string; primary: boolean; accessRole: string }> }
      >(functions, 'listMicrosoftCalendars');
      const r = await fn({});
      return r.data.items;
    },
  });
}

export function useDisconnectMicrosoft() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable<unknown, { ok: true }>(functions, 'microsoftOauthDisconnect');
      await fn({});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['microsoft-auth-status', targetUid] });
      qc.invalidateQueries({ queryKey: ['microsoft-calendars', targetUid] });
    },
  });
}

export function useSetActiveMicrosoftCalendar() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  return useMutation({
    mutationFn: async (calendarId: string) => {
      const fn = httpsCallable<{ calendarId: string }, { ok: true }>(
        functions,
        'setActiveMicrosoftCalendar',
      );
      await fn({ calendarId });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['microsoft-auth-status', targetUid] }),
  });
}

/**
 * Mirrors `startGoogleOauthPopup` — invokes the `microsoftOauthStart`
 * callable to mint a CSRF-safe state nonce and consent URL, then opens
 * the popup. Listener + interval cleanup on every settle path.
 */
export async function startMicrosoftOauthPopup(): Promise<{ ok: boolean }> {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign-in required.');
  const start = httpsCallable<unknown, { url: string; state: string }>(
    functions,
    'microsoftOauthStart',
  );
  const startResp = await start({});
  const popup = window.open(startResp.data.url, 'cp3-microsoft-oauth', 'width=520,height=640,popup=yes');
  if (!popup) throw new Error('Popup blockiert. Bitte Pop-Ups erlauben.');

  return new Promise((resolve) => {
    let interval: number | null = null;
    function cleanup() {
      window.removeEventListener('message', onMessage);
      if (interval !== null) {
        window.clearInterval(interval);
        interval = null;
      }
    }
    function onMessage(ev: MessageEvent<unknown>) {
      if (ev.origin !== window.location.origin && ev.origin !== 'https://cp-3.web.app') {
        return;
      }
      const data = ev.data as { source?: string; ok?: boolean };
      if (data?.source === 'cp3-microsoft-oauth') {
        cleanup();
        resolve({ ok: !!data.ok });
      }
    }
    window.addEventListener('message', onMessage);
    interval = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        resolve({ ok: false });
      }
    }, 800);
  });
}
